import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast, ToastAction } from "@altimateai/lego";
import { useNavigate, useParams } from "react-router-dom";
import { updatePlaygroundSessionStatus } from "@/helpers/apis";
import { PlaygroundAllSessionResponse, SessionLifecycleStatus } from "./types";

const PLAYGROUND_SESSIONS_KEY = "playground-sessions";
const UNDO_TOAST_DURATION_MS = 5000;

/**
 * Removes the given session_ids from every cached page of every variant of
 * the sidebar query (active / archived / search permutations). Returns a
 * snapshot of the previous cache so callers can roll back on error.
 */
const removeFromAllSessionCaches = (
  queryClient: ReturnType<typeof useQueryClient>,
  sessionIds: Set<string>
) => {
  const queries = queryClient.getQueriesData<{
    pages: PlaygroundAllSessionResponse[];
    pageParams: unknown[];
  }>({ queryKey: [PLAYGROUND_SESSIONS_KEY] });

  const snapshot: typeof queries = queries.map(([key, data]) => [key, data]);

  queries.forEach(([key, data]) => {
    if (!data?.pages) return;
    queryClient.setQueryData(key, {
      ...data,
      pages: data.pages.map(page => ({
        ...page,
        sessions: page.sessions.filter(s => !sessionIds.has(s.session_id)),
      })),
    });
  });

  return snapshot;
};

const restoreSessionCaches = (
  queryClient: ReturnType<typeof useQueryClient>,
  snapshot: ReturnType<typeof removeFromAllSessionCaches>
) => {
  snapshot.forEach(([key, data]) => {
    queryClient.setQueryData(key, data);
  });
};

const verbForStatus = (status: SessionLifecycleStatus): string => {
  if (status === "archived") return "archived";
  if (status === "deleted") return "deleted";
  return "restored";
};

interface SingleMutationContext {
  snapshot: ReturnType<typeof removeFromAllSessionCaches>;
  previousStatus: SessionLifecycleStatus;
}

interface SingleMutationVars {
  sessionId: string;
  status: SessionLifecycleStatus;
  previousStatus: SessionLifecycleStatus;
}

/**
 * SCOPE: single-session lifecycle only.
 *
 * The optimistic snapshot/rollback below is correct under the explicit
 * product constraint that the UI never triggers two lifecycle mutations
 * concurrently — there is no bulk-archive / bulk-delete / multi-select
 * action in the sidebar, and per a product decision none is planned.
 *
 * If that ever changes, this hook MUST be revisited before adding a
 * bulk path: each ``onMutate`` snapshots the cache as-it-stands, so
 * overlapping mutations can produce inconsistent rollbacks. Serialize
 * via React Query ``mutationKey`` + cancel semantics, or use a queue,
 * before exposing any concurrent-trigger UI.
 */
export const useSingleSessionStatusMutation = () => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { session_id: openSessionId } = useParams();

  return useMutation<unknown, Error, SingleMutationVars, SingleMutationContext>({
    mutationFn: ({ sessionId, status }) => updatePlaygroundSessionStatus(sessionId, status),
    onMutate: async ({ sessionId, previousStatus }) => {
      await queryClient.cancelQueries({ queryKey: [PLAYGROUND_SESSIONS_KEY] });
      const snapshot = removeFromAllSessionCaches(queryClient, new Set([sessionId]));
      if (openSessionId === sessionId) {
        navigate("/studio");
      }
      return { snapshot, previousStatus };
    },
    onError: (error, _vars, context) => {
      if (context) restoreSessionCaches(queryClient, context.snapshot);
      toast({
        variant: "destructive",
        title: "Action failed",
        description: error.message || "Could not update conversation",
      });
    },
    onSuccess: (_data, { sessionId, status, previousStatus }) => {
      const verb = verbForStatus(status);
      // Archive (and unarchive) → undo-toast. Delete → confirmation modal,
      // so no undo here.
      if (status === "deleted") {
        toast({ title: `Conversation ${verb}` });
        return;
      }
      toast({
        title: `Conversation ${verb}`,
        action: (
          <ToastAction
            altText="Undo"
            onClick={() =>
              updatePlaygroundSessionStatus(sessionId, previousStatus)
                .catch((error: Error) =>
                  toast({
                    variant: "destructive",
                    title: "Undo failed",
                    description: error?.message || "Could not restore conversation",
                  })
                )
                .finally(() =>
                  queryClient.invalidateQueries({ queryKey: [PLAYGROUND_SESSIONS_KEY] })
                )
            }
          >
            Undo
          </ToastAction>
        ),
        duration: UNDO_TOAST_DURATION_MS,
      });
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: [PLAYGROUND_SESSIONS_KEY] });
    },
  });
};
